import { Component, Input, OnChanges, SimpleChanges, OnInit, ViewChild, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';
import { NgClass, NgIf } from '@angular/common';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isoWeek from 'dayjs/plugin/isoWeek';
import { Chart, ChartConfiguration, TooltipItem, registerables } from 'chart.js';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isoWeek);

// It's good practice to register Chart.js components
Chart.register(...registerables);

@Component({
  selector: 'app-order-history-graph',
  imports: [NgIf, NgClass],
  templateUrl: './order-history-graph.component.html',
  styleUrl: './order-history-graph.component.css'
})
export class OrderHistoryGraphComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() data: any[] = [];

  activeTab: 'week' | 'month' | 'year' = 'week';
  activeDateLabel: string = '';

  @ViewChild('weekCanvas') weekCanvas?: ElementRef;
  @ViewChild('monthCanvas') monthCanvas?: ElementRef;
  @ViewChild('yearCanvas') yearCanvas?: ElementRef;

  private chart?: Chart;
  private currentDate = dayjs();

  ngOnInit(): void {
    this.updateLabel();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.data) {
      // If data changes, filter and update the chart.
      // ngAfterViewInit will handle the initial creation.
      if (this.chart) {
        this.filterAndDrawChart();
      }
    }
  }

  ngAfterViewInit(): void {
    // Initially create the chart after the view is ready
    this.filterAndDrawChart();
  }

  ngOnDestroy(): void {
    // Clean up the chart instance when the component is destroyed
    this.destroyChart();
  }

  setActiveTab(tab: 'week' | 'month' | 'year') {
    if (this.activeTab === tab) {
      return; // Do nothing if the tab is already active
    }
    this.activeTab = tab;
    this.updateLabel();

    // When the tab changes, the old canvas is destroyed by *ngIf.
    // We must destroy the old chart and create a new one for the new canvas.
    // A timeout ensures the new canvas is in the DOM before we try to use it.
    setTimeout(() => {
      this.filterAndDrawChart();
    }, 0);
  }

  navigate(direction: 'prev' | 'next') {
    switch (this.activeTab) {
      case 'week':
        this.currentDate = direction === 'prev' ? this.currentDate.subtract(1, 'week') : this.currentDate.add(1, 'week');
        break;
      case 'month':
        this.currentDate = direction === 'prev' ? this.currentDate.subtract(1, 'month') : this.currentDate.add(1, 'month');
        break;
      case 'year':
        this.currentDate = direction === 'prev' ? this.currentDate.subtract(1, 'year') : this.currentDate.add(1, 'year');
        break;
    }
    this.updateLabel();
    this.filterAndDrawChart();
  }

  private updateLabel() {
    switch (this.activeTab) {
      case 'week':
        this.activeDateLabel = `${this.currentDate.startOf('isoWeek').format('DD MMM')} - ${this.currentDate.endOf('isoWeek').format('DD MMM YYYY')}`;
        break;
      case 'month':
        this.activeDateLabel = this.currentDate.format('MMMM YYYY');
        break;
      case 'year':
        this.activeDateLabel = this.currentDate.format('YYYY');
        break;
    }
  }

  private filterAndDrawChart() {
    if (!this.data) return;

    const filteredOrders = this.data.filter((order: any) => {
      const created = dayjs(order.created_at);
      switch (this.activeTab) {
        case 'week': return created.isSame(this.currentDate, 'isoWeek');
        case 'month': return created.isSame(this.currentDate, 'month');
        case 'year': return created.isSame(this.currentDate, 'year');
        default: return true;
      }
    });

    let completed = 0, pending = 0, rejected = 0;
    filteredOrders.forEach((order: any) => {
      switch (order.status?.toLowerCase()) {
        case 'completed': completed++; break;
        case 'pending': pending++; break;
        case 'rejected': rejected++; break;
      }
    });

    // Since the canvas might change, we always destroy the old chart
    // and create a new one with the fresh data.
    this.createChart(pending, rejected, completed);
  }

  private createChart(pending: number, rejected: number, completed: number) {
    // Always destroy the previous chart instance before creating a new one
    this.destroyChart();

    let canvasEl: HTMLCanvasElement | undefined;
    if (this.activeTab === 'week') canvasEl = this.weekCanvas?.nativeElement;
    if (this.activeTab === 'month') canvasEl = this.monthCanvas?.nativeElement;
    if (this.activeTab === 'year') canvasEl = this.yearCanvas?.nativeElement;

    if (!canvasEl) return;
    const ctx = canvasEl.getContext('2d');
    if (!ctx) return;

    const gradients = [
        ['#f3d47fff', '#ffbb00ff'],
        ['#e2c678ff', '#f55454ff'],
        ['#26ee40ff', '#2e8a20ff']
      ].map(([start, end]) => {
        const grad = ctx.createLinearGradient(0, 0, canvasEl?.width ?? 0, canvasEl?.height ?? 0);
        grad.addColorStop(0, start);
        grad.addColorStop(1, end);
        return grad;
      });

      const data = {
        labels: ['Pending', 'Rejected', 'Completed'],
        datasets: [{
          label: 'Orders',
          data: [pending, rejected, completed],
          backgroundColor: gradients,
          borderWidth: 0
        }]
      };

      const config: ChartConfiguration<'doughnut'> = {
        type: 'doughnut',
        data: data,
        options: {
            cutout: '65%',
            responsive: true,
            plugins: {
                tooltip: {
                    backgroundColor: '#FFF6DB',
                    titleColor: '#444444',
                    bodyColor: '#444444',
                    borderColor: '#898989',
                    borderWidth: 1,
                    displayColors: false,
                    yAlign: 'bottom',
                    callbacks: {
                        label: (tooltipItem: TooltipItem<'doughnut'>) => {
                            const value = typeof tooltipItem.raw === 'number' ? tooltipItem.raw : 0;
                            return `${value} Orders`;
                        }
                    }
                },
                legend: {
                    display: true,
                    position: 'right',
                    labels: {
                        usePointStyle: true,
                        pointStyle: 'rectRounded',
                        boxWidth: 12,
                        font: { size: 11 }
                    }
                }
            }
        }
    };

    this.chart = new Chart(ctx, config);
  }

  private destroyChart() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = undefined;
    }
  }
}
